import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { environment } from '@env/environment';
import { Router } from '@angular/router';

export interface FullCourseData {
    duration_hours?: number;
    skills?: string[];
    provider_names?: string[];
    provider_name?: string; // For external-content
    difficulty_level?: string;
    content_type?: string;
    market_trends?: string[];
    professional_expansion?: string[];
    content_id?: string | number;
}

export interface Course {
    id: string;
    title: string;
    description: string;
    level: 'beginner' | 'intermediate' | 'advanced';
    duration: string; // e.g., "4 weeks"
    skills: string[];
    thumbnail?: string;
    reason?: string;
    relevanceScore?: number;
    providerNames?: string[];
    contentId?: string | number; // ID to use for subscription (from full_data.content_id)
    contentType?: string; // instance, external-content, journey
    provider?: string; // For external-content (e.g., 'netzun')
    locale?: string; // Idioma del contenido (e.g., 'es', 'en')
    marketTrends?: string; // Tendencias del mercado
    professionalExpansion?: string; // Expansión profesional
    templateId?: string | number; // Template ID for course type (from API template_id)
    fullData?: FullCourseData;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: number;
    type?: 'text' | 'course-suggestion' | 'faq';
    courses?: Course[]; // Cursos asociados al mensaje de tipo 'course-suggestion'
}

export interface TicketData {
    subject: string;
    description: string;
    conversation: ChatMessage[];
    userEmail?: string;
    priority?: 'low' | 'medium' | 'high';
}

export interface SupportFAQ {
    id: string;
    question: string;
    answer: string;
    category: string;
}

// Interfaces para la respuesta de la API de recomendaciones
interface ApiRecommendationResponse {
    success: boolean;
    data: {
        user_query: string;
        language: string;
        recommendations: ApiCourseRecommendation[];
    };
}

interface ApiCourseRecommendation {
    id: number;
    type: string; // "external_content", "instance", "journey"
    content_id: number;
    title: string;
    description: string;
    main_category: string;
    sub_category: string;
    skills: string[];
    difficulty_level: string;
    duration_hours: number | null;
    keywords: string;
    related_topics: string[];
    is_active: boolean;
    locale: string;
    provider_names: string[];
    company_id: number[];
    camp_ids: Record<string, number[]>;
    reason: string;
    relevance_score: number;
    learning_path_position: number;
    market_trends: string;
    professional_expansion: string;
    created_at: string;
    updated_at: string;
    taxonomy_updated_at: string;
    template_id?: number; // Template ID for course type
}

@Injectable({
    providedIn: 'root'
})
export class ChatAIService {
    private readonly CHAT_STORAGE_KEY = 'msc_chat_history';
    private readonly SUPPORT_STORAGE_KEY = 'msc_support_history';
    private readonly MAX_STORAGE_ITEMS = 100;

    private chatHistorySubject = new BehaviorSubject<ChatMessage[]>([]);
    public chatHistory$ = this.chatHistorySubject.asObservable();

    // Dummy data para cursos sugeridos
    private dummyCourses: Course[] = [
        {
            id: 'course-001',
            title: 'Introducción a JavaScript',
            description: 'Aprende los fundamentos de JavaScript desde cero. Cubrimos variables, funciones, objetos y más.',
            level: 'beginner',
            duration: '2 semanas',
            skills: ['JavaScript', 'Programación', 'Web Development']
        },
        {
            id: 'course-002',
            title: 'Angular Avanzado',
            description: 'Domina patrones avanzados en Angular, RxJS, state management y lazy loading.',
            level: 'advanced',
            duration: '6 semanas',
            skills: ['Angular', 'TypeScript', 'RxJS', 'Frontend']
        },
        {
            id: 'course-003',
            title: 'Node.js y Express',
            description: 'Construye aplicaciones backend escalables con Node.js y Express.',
            level: 'intermediate',
            duration: '4 semanas',
            skills: ['Node.js', 'Express', 'Backend', 'REST API']
        },
        {
            id: 'course-004',
            title: 'SQL y Bases de Datos',
            description: 'Aprende diseño de bases de datos, SQL y mejores prácticas.',
            level: 'intermediate',
            duration: '3 semanas',
            skills: ['SQL', 'Bases de Datos', 'MySQL', 'Data Management']
        },
        {
            id: 'course-005',
            title: 'React Moderno',
            description: 'Domina React con hooks, context API y mejores prácticas.',
            level: 'intermediate',
            duration: '5 semanas',
            skills: ['React', 'JavaScript', 'Frontend', 'Hooks']
        },
        {
            id: 'course-006',
            title: 'Python para Data Science',
            description: 'Usa Python, Pandas y Scikit-learn para análisis de datos.',
            level: 'advanced',
            duration: '8 semanas',
            skills: ['Python', 'Data Science', 'Machine Learning', 'Analytics']
        }
    ];

    // Dummy data para FAQs de soporte
    private dummyFAQs: SupportFAQ[] = [
        /* {
            id: 'faq-001',
            question: '¿Cómo cambio mi contraseña?',
            answer: 'Para cambiar tu contraseña, ve a Configuración > Seguridad > Cambiar Contraseña. Ingresa tu contraseña actual y la nueva contraseña dos veces.',
            category: 'Cuenta'
        }, */
        {
            id: 'faq-002',
            question: '¿Cómo me suscribo a un curso?',
            answer: 'Busca el curso deseado, haz clic en "Inscribirse" y sigue los pasos. Tendrás acceso inmediato al contenido.',
            category: 'Cursos'
        },
        {
            id: 'faq-003',
            question: '¿Puedo descargar los materiales del curso?',
            answer: 'Sí, la mayoría de cursos permiten descargar PDFs y recursos. Busca el botón "Descargar" en los contenidos.',
            category: 'Cursos'
        },
        /* {
            id: 'faq-004',
            question: '¿Cuál es la política de reembolso?',
            answer: 'Ofrecemos reembolso completo dentro de 30 días de la compra si no has accedido significativamente al contenido.',
            category: 'Facturación'
        },
        {
            id: 'faq-005',
            question: '¿Cómo contacto al equipo de soporte?',
            answer: 'Puedes abrir un ticket de soporte directamente desde esta ventana de chat haciendo clic en "No resolvió mi problema".',
            category: 'Soporte'
        },*/
        {
            id: 'faq-006',
            question: '¿Hay certificados disponibles?',
            answer: 'Sí, al completar un curso recibirás un certificado digital que puedes compartir en LinkedIn u otros perfiles.',
            category: 'Certificación'
        }
    ];

    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private router: Router
    ) {
        this.loadChatHistory();
    }

    /**
     * Obtiene sugerencias de cursos basado en la entrada del usuario
     * Llama a la API de recomendaciones real
     */
    getSuggestedCourses(userInput: string): Observable<Course[]> {
        try {
            if (!userInput || !userInput.trim()) {
                console.warn('Empty user input provided to getSuggestedCourses');
                return of([]);
            }

            const company = this.storageService.get('company');
            const companyId = company?.id || 985; // Fallback a 985 si no hay company

            const payload = {
                company_id: companyId,
                query: userInput.trim(),
                min_recommendations: 3,
                max_recommendations: 8,
                preferred_category: null,
                difficulty_level: null
            };

            console.log('📤 Sending recommendations request:', { companyId, query: userInput });

            // Construir la URL completa basada en el environment
            // API_URL ya incluye /api, entonces solo agregamos el endpoint
            const baseApiUrl = environment.envVar.API_URL.endsWith('/')
                ? environment.envVar.API_URL
                : `${environment.envVar.API_URL}/`;
            const apiUrl = `${baseApiUrl}recommendations/courses`;

            return this.http.post<ApiRecommendationResponse>(apiUrl, payload).pipe(
                map(response => {
                    console.log('📥 API Response:', response);

                    // Validar estructura básica de respuesta
                    if (!response) {
                        console.error('❌ Response is null or undefined');
                        throw new Error('Empty response from server');
                    }

                    if (response.success === false) {
                        console.warn('⚠️ API returned success: false');
                        return [];
                    }

                    if (!response.data || !Array.isArray(response.data.recommendations)) {
                        console.warn('⚠️ Invalid response structure - no recommendations array');
                        return [];
                    }

                    const recommendations = response.data.recommendations;

                    if (recommendations.length === 0) {
                        console.log('ℹ️ No recommendations returned from API');
                        return [];
                    }

                    console.log(`✅ Processing ${recommendations.length} recommendations`);

                    // Mapear la respuesta de la API a la interfaz Course
                    const mappedCourses = recommendations.map(rec => {
                        try {
                            console.log('🔄 Mapping recommendation:', rec);

                            // Obtener el primer proveedor
                            const providerName = rec.provider_names?.[0];

                            return {
                                id: rec.id.toString(),
                                title: rec.title,
                                description: rec.description || rec.reason || rec.title,
                                level: this.mapDifficultyLevel(rec.difficulty_level),
                                duration: rec.duration_hours ? `${rec.duration_hours} horas` : 'Duración variable',
                                skills: rec.skills || [],
                                reason: rec.reason,
                                relevanceScore: rec.relevance_score,
                                providerNames: rec.provider_names || [],
                                contentId: rec.content_id,
                                contentType: rec.type || 'instance',
                                provider: providerName,
                                locale: rec.locale,
                                marketTrends: rec.market_trends,
                                professionalExpansion: rec.professional_expansion,
                                templateId: rec.template_id,
                                fullData: {
                                    duration_hours: rec.duration_hours,
                                    skills: rec.skills,
                                    provider_names: rec.provider_names,
                                    difficulty_level: rec.difficulty_level,
                                    content_type: rec.type,
                                    market_trends: [rec.market_trends],
                                    professional_expansion: [rec.professional_expansion],
                                    content_id: rec.content_id
                                }
                            } as Course;
                        } catch (mapError) {
                            console.error('❌ Error mapping recommendation:', mapError, rec);
                            return null;
                        }
                    }).filter((course) => course !== null) as Course[];

                    if (mappedCourses.length === 0) {
                        console.warn('⚠️ No valid courses after mapping');
                        return [];
                    }

                    return mappedCourses;
                }),
                catchError(error => {
                    console.error('❌ Error fetching course recommendations:', {
                        status: error.status,
                        statusText: error.statusText,
                        message: error.message,
                        error: error.error
                    });

                    // Re-lanzar el error para que el componente lo maneje
                    // y pueda mostrar mensajes específicos según el tipo de error
                    throw error;
                })
            );
        } catch (error) {
            console.error('❌ Exception in getSuggestedCourses:', error);
            // Retornar un observable vacío para errores síncronos
            return throwError(() => error);
        }
    }

    /**
     * Mapea el nivel de dificultad de la API a la interfaz Course
     */
    private mapDifficultyLevel(apiLevel: string): 'beginner' | 'intermediate' | 'advanced' {
        const level = apiLevel?.toLowerCase() || 'intermediate';
        if (level.includes('advanced') || level.includes('expert')) {
            return 'advanced';
        }
        if (level.includes('beginner') || level.includes('basic')) {
            return 'beginner';
        }
        return 'intermediate';
    }

    /**
     * Obtiene una respuesta de FAQ de soporte
     * Simula búsqueda en base de conocimientos
     */
    getSupportResponse(userQuestion: string): Observable<SupportFAQ[]> {
        return new Observable(observer => {
            setTimeout(() => {
                const query = userQuestion.toLowerCase();
                const matches = this.dummyFAQs.filter(faq => {
                    return (
                        faq.question.toLowerCase().includes(query) ||
                        faq.answer.toLowerCase().includes(query) ||
                        faq.category.toLowerCase().includes(query)
                    );
                });

                observer.next(matches);
                observer.complete();
            }, 600);
        });
    }

    /**
     * Obtiene todas las FAQs
     */
    getAllFAQs(): SupportFAQ[] {
        return this.dummyFAQs;
    }

    /**
     * Carga el historial del chat desde localStorage
     */
    private loadChatHistory(): void {
        const stored = localStorage.getItem(this.CHAT_STORAGE_KEY);
        if (stored) {
            try {
                const messages = JSON.parse(stored);
                // Eliminar duplicados antes de cargar
                const uniqueMessages = this.removeDuplicateMessages(messages);
                this.chatHistorySubject.next(uniqueMessages);
                // Guardar la versión limpia
                if (messages.length !== uniqueMessages.length) {
                    this.saveChatHistory(uniqueMessages);
                }
            } catch (e) {
                console.error('Error loading chat history', e);
                this.chatHistorySubject.next([]);
            }
        }
    }

    /**
     * Elimina mensajes duplicados del historial
     */
    private removeDuplicateMessages(messages: ChatMessage[]): ChatMessage[] {
        const seen = new Map<string, ChatMessage>();

        messages.forEach(msg => {
            // Usar el ID como clave única, o una combinación de contenido + timestamp si no hay ID
            const key = msg.id || `${msg.content}-${msg.role}-${msg.timestamp}`;

            // Solo agregar si no hemos visto este mensaje antes
            if (!seen.has(key)) {
                seen.set(key, msg);
            }
        });

        return Array.from(seen.values());
    }

    /**
     * Obtiene el historial del chat
     */
    getChatHistory(): ChatMessage[] {
        return this.chatHistorySubject.value;
    }

    /**
     * Añade un mensaje al historial del chat
     */
    addChatMessage(message: ChatMessage): void {
        const history = this.getChatHistory();

        // Verificar si el mensaje ya existe (evitar duplicados)
        const isDuplicate = history.some(msg =>
            msg.id === message.id ||
            (msg.content === message.content &&
             msg.role === message.role &&
             Math.abs(msg.timestamp - message.timestamp) < 1000) // Dentro de 1 segundo
        );

        if (!isDuplicate) {
            history.push(message);

            // Mantener límite de almacenamiento
            if (history.length > this.MAX_STORAGE_ITEMS) {
                history.shift();
            }

            this.chatHistorySubject.next(history);
            this.saveChatHistory(history);
        }
    }

    /**
     * Guarda el historial del chat en localStorage
     */
    private saveChatHistory(messages: ChatMessage[]): void {
        try {
            localStorage.setItem(this.CHAT_STORAGE_KEY, JSON.stringify(messages));
        } catch (e) {
            console.error('Error saving chat history', e);
        }
    }

    /**
     * Limpia el historial del chat
     */
    clearChatHistory(): void {
        this.chatHistorySubject.next([]);
        localStorage.removeItem(this.CHAT_STORAGE_KEY);
    }

    /**
     * Abre un ticket de soporte
     * Envía la conversación al backend
     */
    openTicket(ticketData: TicketData): Observable<{ success: boolean; ticketId: string; message: string }> {
        return new Observable(observer => {
            // Simular envío al backend
            setTimeout(() => {
                const ticketId = this.generateTicketId();
                const response = {
                    success: true,
                    ticketId: ticketId,
                    message: `Ticket #${ticketId} creado exitosamente. Pronto nos pondremos en contacto.`
                };

                // Guardar en localStorage como referencia
                this.saveTicketToStorage(ticketId, ticketData);

                observer.next(response);
                observer.complete();
            }, 1000);
        });
    }

    /**
     * Genera un ID único para el ticket
     */
    private generateTicketId(): string {
        return `TKT-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    }

    /**
     * Guarda el ticket en localStorage
     */
    private saveTicketToStorage(ticketId: string, data: TicketData): void {
        try {
            const tickets = JSON.parse(localStorage.getItem(this.SUPPORT_STORAGE_KEY) || '[]');
            tickets.push({
                ticketId,
                createdAt: new Date().toISOString(),
                ...data
            });
            localStorage.setItem(this.SUPPORT_STORAGE_KEY, JSON.stringify(tickets));
        } catch (e) {
            console.error('Error saving ticket', e);
        }
    }

    /**
     * Obtiene lista de tickets creados
     */
    getTickets(): Array<{ ticketId: string; createdAt: string }> {
        try {
            return JSON.parse(localStorage.getItem(this.SUPPORT_STORAGE_KEY) || '[]');
        } catch (e) {
            return [];
        }
    }

    /**
     * Navega al detalle del curso basado en el tipo de contenido
     * Soporta: instance, external-content, journey, course
     *
     * Para contentType 'course': usa templateId como ID principal
     * Para otros tipos: usa courseId
     */
    navigateToCourseDetail(courseId: string | number, contentType = 'instance', provider?: string, templateId?: string | number): void {
        try {
            const company = this.storageService.get('company');
            if (company) {
                const slug = company.slug || 'learn';
                let navigationPath: (string | number)[] = [];

                // Debug: Log los valores para verificar
                console.log('navigateToCourseDetail:', { courseId, contentType, provider, templateId });

                // Normalizar el contentType para comparación
                const normalizedType = contentType?.toLowerCase().trim() || 'instance';

                switch (normalizedType) {
                    case 'external-content':
                    case 'external_content':
                    case 'externalcontent':
                        // URL: /learn/{slug}/detail/external-content/{provider}/{contentId}/presentation
                        // Nota: La API devuelve "external_content" pero la URL usa "external-content"
                        navigationPath = ['/learn', slug, 'detail', 'external-content', provider || 'external', courseId, 'presentation'];
                        break;
                    case 'journey':
                    case 'trayecto':
                    case 'traject':
                        // URL: /learn/{slug}/detail/journey/{journeyId}/presentation
                        // Nota: la API devuelve "traject" (nombre del modelo), no "journey"
                        navigationPath = ['/learn', slug, 'detail', 'journey', courseId, 'presentation'];
                        break;
                    case 'course': {
                        // URL: /learn/{slug}/detail/instance/{templateId} - Usa templateId para cursos
                        const idToUse = templateId || courseId;
                        navigationPath = ['/learn', slug, 'detail', 'instance', idToUse];
                        break;
                    }
                    case 'instance':
                    case 'curso':
                    default:
                        // URL: /learn/{slug}/detail/instance/{instanceId}
                        navigationPath = ['/learn', slug, 'detail', 'instance', courseId];
                        break;
                }

                console.log('Navigation path:', navigationPath);
                this.router.navigate(navigationPath);
            }
        } catch (error) {
            console.error('Error navigating to course detail:', error);
        }
    }

    /**
     * Abre el catálogo de cursos en una nueva ventana
     */
    openCourseCatalog(): void {
        try {
            const company = this.storageService.get('company');
            if (company) {
                const slug = company.slug || 'learn';
                const catalogUrl = `${window.location.origin}/learn/${slug}/catalogue`;
                window.open(catalogUrl, '_blank');
            }
        } catch (error) {
            console.error('Error opening catalog:', error);
        }
    }
}

